// LibrarySidebarSection.jsx

import React, { useCallback, useMemo } from 'react';

// Context
import { useNodeTypes } from 'context/NodeTypesContext';
import { useUI } from 'context/UIContext';

// Utilities
import { NODE_MODULE } from 'utilities/nodeModule';
import { setPendingCreateNodeModule } from 'utilities/createNodeModuleContext';
import { partitionLibrarySidebarDatabases } from 'main/sidebar/buckets/utils/bucketTransforms';

// CSS
import './LibrarySidebarSection.css';

// Components
import FavoritesSidebarSection from 'main/sidebar/buckets/favorites/FavoritesSidebarSection';
import SidebarBucketAddButton from 'main/sidebar/buckets/addbutton/SidebarBucketAddButton';
import SidebarButton from 'main/sidebar/button/SidebarButton';

/**
 * LibrarySidebarSection
 *
 * PINNED — pinned nodes for the active base (above Menu when present).
 * MENU — types with Show in Menu (non-media).
 * CHANNELS — optional slot rendered between MENU and MEDIA.
 * MEDIA — Photos / Videos / Documents / Files.
 * COLLECTIONS — all custom collections + New Collection (always navigable).
 *
 * @param {ReactNode} [afterMenu] - Optional section rendered between MENU and MEDIA.
 * @param {array} [databases] - Library-visible collection rows for this base.
 * @param {function} [isDatabaseSelected] - Whether a collection key is selected.
 * @param {function} [onAddDatabase] - Opens AddDatabaseModal to create a collection.
 * @param {function} [onDatabaseClick] - Opens a collection (database, module).
 *
 * @returns {ReactNode} The rendered sidebar section.
 */
const LibrarySidebarSection = React.memo(({
	afterMenu = null,
	databases = [],
	isDatabaseSelected,
	onAddDatabase,
	onDatabaseClick,
}) => {

	// ----- CONTEXT ----- //

	const { getType } = useNodeTypes();
	const { isSidebarRailCompact } = useUI();
	const compact = Boolean(isSidebarRailCompact);

	// ----- MEMOS ----- //

	const {
		menuPrimary,
		menuMedia,
		customCollections,
	} = useMemo(
		() => partitionLibrarySidebarDatabases(databases, getType),
		[databases, getType],
	);

	const hasMenu = menuPrimary.length > 0;
	const hasMedia = menuMedia.length > 0;
	const hasCollections = customCollections.length > 0;

	// ----- HANDLERS ----- //

	/**
	 * handleAddDatabaseClick
	 *
	 * Scopes create to the private module and opens AddDatabaseModal.
	 */
	const handleAddDatabaseClick = useCallback(() => {
		setPendingCreateNodeModule(NODE_MODULE.PRIVATE);
		onAddDatabase?.();
	}, [onAddDatabase]);

	const isRowSelected = useCallback((row) => {
		if (typeof isDatabaseSelected !== 'function') return false;
		return Boolean(isDatabaseSelected(row?.databaseKey || row?.key, row?.appearAsKey));
	}, [isDatabaseSelected]);

	const renderDatabaseRows = useCallback((rows, keyPrefix) => (
		<div className="sidebar-bucket-list">
			{rows.map((row) => (
				<SidebarButton
					key={`${keyPrefix}-${row.key}`}
					iconKey={row.iconKey}
					iconResource={row.iconResource}
					label={row.label}
					selected={isRowSelected(row)}
					onClick={() => onDatabaseClick?.(
						row.database,
						NODE_MODULE.PRIVATE,
						{ appearAsKey: row.appearAsKey },
					)}
				/>
			))}
		</div>
	), [isRowSelected, onDatabaseClick]);

	// ----- RENDER ----- //

	return (
		<div
			className={[
				'library-sidebar-section-container',
				compact ? 'is-rail-compact' : '',
			].filter(Boolean).join(' ')}
		>

			{/* PINNED — pinned nodes for this base */}
			<FavoritesSidebarSection />

			{/* MENU — types with Show in Menu (non-media) */}
			{hasMenu ? (
				<div className="library-sidebar-section-block">
					{!compact ? (
						<div className="sidebar-bucket-heading">
							<div className="sidebar-bucket-subtitle library-sidebar-section-title-row">
								<span>Menu</span>
							</div>
						</div>
					) : null}

					{renderDatabaseRows(menuPrimary, 'menu')}
				</div>
			) : null}

			{afterMenu ? (
				<div className="library-sidebar-section-block">
					{afterMenu}
				</div>
			) : null}

			{/* MEDIA — Photos / Videos / Documents / Files */}
			{hasMedia ? (
				<div className="library-sidebar-section-block">
					{!compact ? (
						<div className="sidebar-bucket-heading">
							<div className="sidebar-bucket-subtitle library-sidebar-section-title-row">
								<span>Media</span>
							</div>
						</div>
					) : null}

					{renderDatabaseRows(menuMedia, 'media')}
				</div>
			) : null}

			{/* COLLECTIONS — custom types (always listed) */}
			{(hasCollections || !compact) ? (
				<div className="library-sidebar-section-block">
					{!compact ? (
						<div className="sidebar-bucket-heading">
							<div className="sidebar-bucket-subtitle library-sidebar-section-title-row">
								<span>Collections</span>
							</div>
						</div>
					) : null}

					{hasCollections
						? renderDatabaseRows(customCollections, 'collections')
						: null}

					{!compact ? (
						<SidebarBucketAddButton
							label="New Collection"
							onClick={handleAddDatabaseClick}
						/>
					) : null}
				</div>
			) : null}

		</div>
	);
});

LibrarySidebarSection.displayName = 'LibrarySidebarSection';

export default LibrarySidebarSection;
